/**
 * Translation + locale-aware formatting.
 *
 * `t()` also carries the formatters, because currency and dates are locale-dependent
 * in exactly the same way text is — a component that has `t` never needs to know which
 * locale is active to render `1.234,56 €` correctly.
 */

import * as React from 'react';
import { formatDate as formatIsoDate, formatDateTime, formatMoney, formatMoneyCompact } from '@ias/shared';

import { dictionaries, type Dictionary, type TranslationKey } from './dictionaries';

export type Locale = keyof typeof dictionaries;
export const LOCALES = Object.keys(dictionaries) as Locale[];

const LOCALE_TAGS: Record<Locale, string> = { el: 'el-GR', en: 'en-GB' };
const STORAGE_KEY = 'ias.locale';

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Looks up a key; interpolates `{name}` placeholders. */
  t: (key: TranslationKey | string, vars?: Record<string, string | number>) => string;
  /**
   * Count-aware lookup: resolves `<key>.one` or `<key>.other` and passes the count in
   * as `{n}`. Both Greek and English need it — «1 επιλεγμένα» and "1 rows" are equally
   * wrong, and concatenating a number in front of a fixed noun cannot ever be right.
   */
  tn: (key: string, count: number, vars?: Record<string, string | number>) => string;
  /** Enum label lookup: `te('policyStatus', 'renewal_due')`. */
  te: (set: string, value: string | null | undefined) => string;
  money: (value: string | number | null | undefined) => string;
  moneyCompact: (value: string | number | null | undefined) => string;
  date: (value: string | null | undefined) => string;
  dateTime: (value: string | null | undefined) => string;
  number: (value: number | null | undefined, options?: Intl.NumberFormatOptions) => string;
  percent: (value: number | null | undefined, digits?: number) => string;
  /** Relative time ("in 3 days", "πριν 2 ημέρες") for ageing and countdowns. */
  relativeDays: (days: number) => string;
}

const I18nContext = React.createContext<I18nValue | null>(null);

function detectLocale(): Locale {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  if (stored && LOCALES.includes(stored as Locale)) return stored as Locale;
  const browser = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : 'el';
  return LOCALES.includes(browser as Locale) ? (browser as Locale) : 'el';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = React.useState<Locale>(detectLocale);

  const setLocale = React.useCallback((next: Locale) => {
    setLocaleState(next);
    localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.lang = next;
  }, []);

  React.useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = React.useMemo<I18nValue>(() => {
    const dict = dictionaries[locale] as unknown as Dictionary;
    const tag = LOCALE_TAGS[locale];

    const t: I18nValue['t'] = (key, vars) => {
      // Falling back to the key itself makes a missing translation visible in the UI
      // rather than rendering an empty string that looks like a layout bug.
      const template = dict[key as string] ?? (dictionaries.el as unknown as Dictionary)[key as string] ?? key;
      if (!vars) return template;
      return template.replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? `{${name}}`));
    };

    const tn: I18nValue['tn'] = (key, count, vars) =>
      t(`${key}.${count === 1 ? 'one' : 'other'}`, { n: count, ...vars });

    return {
      locale,
      setLocale,
      t,
      tn,
      te: (set, enumValue) => (enumValue ? t(`enum.${set}.${enumValue}`) : '—'),
      money: (v) => formatMoney(v, tag),
      moneyCompact: (v) => formatMoneyCompact(v, tag),
      date: (v) => formatIsoDate(v ?? null, tag),
      dateTime: (v) => formatDateTime(v ?? null, tag),
      number: (v, options) =>
        v === null || v === undefined ? '—' : new Intl.NumberFormat(tag, options).format(v),
      percent: (v, digits = 1) =>
        v === null || v === undefined
          ? '—'
          : new Intl.NumberFormat(tag, {
              style: 'percent',
              minimumFractionDigits: digits,
              maximumFractionDigits: digits,
            }).format(v / 100),
      relativeDays: (days) =>
        new Intl.RelativeTimeFormat(tag, { numeric: 'auto' }).format(days, 'day'),
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const context = React.useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside <I18nProvider>');
  return context;
}

/** Shorthand for the common case of needing only the translator. */
export function useT() {
  return useI18n().t;
}

export type { TranslationKey };
